import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Envelope, Users, PaperPlaneRight, ClockCounterClockwise, Storefront, Plus, X, ArrowUp, ArrowDown, Tag, Percent } from "@phosphor-icons/react";

// ==============================================================
// NEWSLETTER PANEL
// ==============================================================
export function NewsletterPanel({ tenant }) {
  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [signature, setSignature] = useState(`Mit freundlichen Grüßen\n${tenant?.name || ""}\n`);
  const [includeRegister, setIncludeRegister] = useState(true);
  const [subs, setSubs] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const registerLink = tenant?.slug ? `${window.location.origin}/portal/${tenant.slug}` : "";

  const load = async () => {
    const [s, c] = await Promise.all([
      api.get("/newsletter/subscribers").catch(() => ({ data: [] })),
      api.get("/newsletter/campaigns").catch(() => ({ data: [] })),
    ]);
    setSubs(s.data || []);
    setCampaigns(c.data || []);
  };
  useEffect(() => { load(); }, []);

  const filteredCount = subs.filter(s => audience === "all" ? true : s.customer_type === audience).length;

  const send = async () => {
    if (!subject.trim() || !body.trim()) return;
    if (!window.confirm(`Newsletter an ${filteredCount} Empfänger versenden?`)) return;
    setSending(true);
    setResult(null);
    try {
      const r = await api.post("/newsletter/send", {
        subject,
        body,
        signature,
        audience,
        register_link: includeRegister ? registerLink : null,
      });
      setResult(r.data);
      setSubject(""); setBody("");
      load();
    } catch (e) {
      setResult({ error: e.response?.data?.detail || "Fehler beim Versand" });
    } finally {
      setSending(false);
    }
  };

  const unsubscribe = async (id) => {
    if (!window.confirm("Empfänger wirklich austragen?")) return;
    await api.delete(`/newsletter/subscribers/${id}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="newsletter-panel">
      <section className="lg:col-span-2 bg-white border border-black/10 p-5 space-y-4">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2"><PaperPlaneRight size={18} weight="bold" /> Newsletter verfassen</h3>

        <div>
          <Label className="text-xs">Empfänger</Label>
          <Select value={audience} onValueChange={setAudience}>
            <SelectTrigger className="h-10" data-testid="newsletter-audience"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle ({subs.length})</SelectItem>
              <SelectItem value="private">Nur Privatkunden ({subs.filter(s => s.customer_type === "private").length})</SelectItem>
              <SelectItem value="business">Nur Geschäftskunden ({subs.filter(s => s.customer_type === "business").length})</SelectItem>
            </SelectContent>
          </Select>
          <div className="text-[11px] text-zinc-500 mt-1">→ {filteredCount} Empfänger erhalten diesen Newsletter</div>
        </div>

        <div>
          <Label className="text-xs">Betreff</Label>
          <Input className="h-10" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="z. B. Sommer-Aktion: 20% auf Zusatzreinigungen" data-testid="newsletter-subject" />
        </div>

        <div>
          <Label className="text-xs">Nachricht</Label>
          <textarea
            className="w-full text-sm p-3 border border-black/10 rounded"
            rows="10"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Liebe Kundin, lieber Kunde…"
            data-testid="newsletter-body"
          />
        </div>

        <div>
          <Label className="text-xs">Signatur</Label>
          <textarea
            className="w-full text-sm p-3 border border-black/10 rounded bg-zinc-50"
            rows="4"
            value={signature}
            onChange={(e) => setSignature(e.target.value)}
            data-testid="newsletter-signature"
          />
        </div>

        <div className="flex items-center gap-2">
          <input type="checkbox" id="incl-reg" checked={includeRegister} onChange={(e) => setIncludeRegister(e.target.checked)} data-testid="newsletter-incl-register" />
          <Label htmlFor="incl-reg" className="text-xs cursor-pointer">Link zur Registrierungs-Seite anhängen: <span className="font-mono text-zinc-500">{registerLink}</span></Label>
        </div>

        <div className="flex items-center justify-between pt-2 border-t border-black/5">
          <div className="text-xs text-zinc-500">
            Vorschau öffnet automatisch bei Empfängern mit HTML-fähigem Client.
          </div>
          <Button onClick={send} disabled={sending || !subject.trim() || !body.trim() || filteredCount === 0} className="bg-black text-white hover:bg-zinc-800 rounded-none h-10" data-testid="newsletter-send">
            {sending ? "Sende…" : `An ${filteredCount} senden`}
          </Button>
        </div>

        {result && (
          <div className={`text-xs p-3 border ${result.error ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`} data-testid="newsletter-result">
            {result.error ? `Fehler: ${result.error}` : `Versendet: ${result.sent} · Fehlgeschlagen: ${result.failed} · Gesamt: ${result.recipients}`}
          </div>
        )}
      </section>

      <div className="space-y-6">
        <section className="bg-white border border-black/10 p-5">
          <h4 className="font-heading text-sm font-semibold mb-3 flex items-center gap-2"><Users size={14} weight="bold" /> Abonnenten ({subs.length})</h4>
          {subs.length === 0 ? <p className="text-xs text-zinc-500">Noch keine Abonnenten. Endkunden können sich auf der Portal-Seite eintragen.</p> : (
            <div className="max-h-64 overflow-y-auto space-y-1">
              {subs.map(s => (
                <div key={s.subscriber_id} className="flex items-center justify-between py-1 text-xs border-b border-black/5" data-testid={`sub-row-${s.subscriber_id}`}>
                  <div>
                    <div className="font-mono">{s.email}</div>
                    <div className="text-[10px] text-zinc-500">{s.customer_type === "private" ? "Privat" : "Geschäft"}{s.company_name && ` · ${s.company_name}`}</div>
                  </div>
                  <button onClick={() => unsubscribe(s.subscriber_id)} className="text-red-500 hover:text-red-700" data-testid={`unsub-${s.subscriber_id}`}><X size={12} /></button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white border border-black/10 p-5">
          <h4 className="font-heading text-sm font-semibold mb-3 flex items-center gap-2"><ClockCounterClockwise size={14} weight="bold" /> Kampagnen-Verlauf</h4>
          {campaigns.length === 0 ? <p className="text-xs text-zinc-500">Noch keine Kampagnen versendet.</p> : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {campaigns.slice(0, 10).map(c => (
                <div key={c.campaign_id} className="text-xs border border-black/5 p-2 rounded" data-testid={`campaign-row-${c.campaign_id}`}>
                  <div className="font-medium">{c.subject}</div>
                  <div className="text-[10px] text-zinc-500">{new Date(c.created_at).toLocaleString("de-DE")} · {c.audience === "all" ? "Alle" : c.audience === "private" ? "Privat" : "Business"} · {c.sent}/{c.recipients_count} versendet</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}


// ==============================================================
// SERVICE STATUS PANEL (in Branding-Tab integriert)
// ==============================================================
export function ServiceStatusPanel({ tenant, onSaved }) {
  const [f, setF] = useState({
    service_status: "available",
    service_available_from: "",
    coming_soon_headline: "",
    coming_soon_subheadline: "",
    coming_soon_message: "",
    coming_soon_hero_image_url: "",
    coming_soon_cta_text: "",
    coming_soon_availability_label: "",
      coming_soon_features: [],
      coming_soon_feature_grid: [],
      coming_soon_feature_grid_title: "",
      coming_soon_process_steps: [],
      coming_soon_process_title: "",
      coming_soon_show_newsletter: true,
      coming_soon_show_contact: true,
  });
  const [saving, setSaving] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState(null);
  const [featureDraft, setFeatureDraft] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = React.useRef(null);
  const [iframeReady, setIframeReady] = useState(false);

  // Draft-Mode Live-Preview: pushe aktuelle Form-Werte per postMessage ins iframe
  useEffect(() => {
    if (!showPreview || !iframeRef.current || !iframeReady) return;
    const win = iframeRef.current.contentWindow;
    if (!win) return;
    try {
      win.postMessage({ type: "tonneva-preview-update", data: f }, window.location.origin);
    } catch (e) { console.warn("[ServiceStatus] preview postMessage failed", e); }
  }, [f, showPreview, iframeReady]);

  // Höre auf „ready"-Signal des iframes
  useEffect(() => {
    const onMessage = (e) => {
      if (e.data?.type === "tonneva-preview-ready") {
        setIframeReady(true);
        // Initial push
        if (iframeRef.current?.contentWindow) {
          iframeRef.current.contentWindow.postMessage({ type: "tonneva-preview-update", data: f }, window.location.origin);
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [f]);

  useEffect(() => {
    setF({
      service_status: tenant?.service_status || "available",
      service_available_from: tenant?.service_available_from || "",
      coming_soon_headline: tenant?.coming_soon_headline || "",
      coming_soon_subheadline: tenant?.coming_soon_subheadline || "",
      coming_soon_message: tenant?.coming_soon_message || "",
      coming_soon_hero_image_url: tenant?.coming_soon_hero_image_url || "",
      coming_soon_cta_text: tenant?.coming_soon_cta_text || "",
      coming_soon_availability_label: tenant?.coming_soon_availability_label || "",
      coming_soon_features: tenant?.coming_soon_features || [],
      coming_soon_feature_grid: tenant?.coming_soon_feature_grid || [],
      coming_soon_feature_grid_title: tenant?.coming_soon_feature_grid_title || "",
      coming_soon_process_steps: tenant?.coming_soon_process_steps || [],
      coming_soon_process_title: tenant?.coming_soon_process_title || "",
      coming_soon_show_newsletter: tenant?.coming_soon_show_newsletter !== false,
      coming_soon_show_contact: tenant?.coming_soon_show_contact !== false,
    });
  }, [tenant]);

  const save = async () => {
    setSaving(true);
    setSavedFeedback(null);
    try {
      const r = await api.patch(`/tenants/${tenant.tenant_id}/service-status`, {
        ...f,
        service_available_from: f.service_available_from || null,
      });
      onSaved && onSaved(r.data);
      setSavedFeedback({ ok: true, msg: "Erfolgreich gespeichert" });
      setPreviewKey(k => k + 1);
      setTimeout(() => setSavedFeedback(null), 3500);
    } catch (e) {
      setSavedFeedback({ ok: false, msg: e.response?.data?.detail || "Fehler beim Speichern" });
    } finally { setSaving(false); }
  };

  const addFeature = () => {
    if (!featureDraft.trim()) return;
    setF({ ...f, coming_soon_features: [...f.coming_soon_features, featureDraft.trim()] });
    setFeatureDraft("");
  };
  const removeFeature = (i) => setF({ ...f, coming_soon_features: f.coming_soon_features.filter((_, idx) => idx !== i) });

  const isComing = f.service_status === "coming_soon";
  const previewUrl = tenant?.slug ? `/portal/${tenant.slug}` : null;

  return (
    <section className="bg-white border border-black/10 p-5 space-y-4" data-testid="service-status-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <Storefront size={18} weight="bold" />
        <h3 className="font-heading text-lg font-semibold tracking-tight">Service-Status & Coming-Soon-Seite</h3>
        <span className={`text-[10px] px-2 py-0.5 rounded-full ml-2 ${!isComing ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
          {!isComing ? "LIVE" : "COMING SOON"}
        </span>
        <label className="ml-auto text-xs flex items-center gap-1.5 cursor-pointer" data-testid="toggle-preview">
          <input type="checkbox" checked={showPreview} onChange={(e) => setShowPreview(e.target.checked)} />
          Live-Vorschau anzeigen
        </label>
      </div>
      <p className="text-xs text-zinc-500">Steuert, ob Endkunden im Portal buchen können oder eine individuell gestaltete Coming-Soon-Seite mit Newsletter-Anmeldung sehen. Nach dem Speichern erscheinen die Änderungen sofort in der Vorschau.</p>

      <div className={showPreview && isComing ? "grid lg:grid-cols-2 gap-6" : ""}>
        <div className="space-y-4">

      <div className="grid md:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs">Status</Label>
          <Select value={f.service_status} onValueChange={(v) => setF({ ...f, service_status: v })}>
            <SelectTrigger className="h-10" data-testid="service-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="available">Verfügbar — Kunden können buchen</SelectItem>
              <SelectItem value="coming_soon">Coming Soon — nur Newsletter</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {isComing && (
          <>
            <div>
              <Label className="text-xs">Verfügbar ab (Datum, optional)</Label>
              <Input type="date" className="h-10" value={f.service_available_from} onChange={(e) => setF({ ...f, service_available_from: e.target.value })} data-testid="service-available-from" />
            </div>
            <div>
              <Label className="text-xs">Alternativer Zeitraum-Text</Label>
              <Input className="h-10" value={f.coming_soon_availability_label} onChange={(e) => setF({ ...f, coming_soon_availability_label: e.target.value })} placeholder={'z. B. „Herbst 2026"'} data-testid="service-availability-label" />
            </div>
          </>
        )}
      </div>

      {isComing && (
        <div className="space-y-4 pt-4 border-t border-black/5">
          <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500">Hero-Bereich</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Große Überschrift (H1)</Label>
              <Input className="h-10" value={f.coming_soon_headline} onChange={(e) => setF({ ...f, coming_soon_headline: e.target.value })} placeholder={'z. B. „Wir starten bald in Ihrer Region"'} data-testid="service-headline" />
            </div>
            <div>
              <Label className="text-xs">Unter-Überschrift</Label>
              <Input className="h-10" value={f.coming_soon_subheadline} onChange={(e) => setF({ ...f, coming_soon_subheadline: e.target.value })} placeholder={'z. B. „Sauber. Umweltfreundlich. Zuverlässig."'} data-testid="service-subheadline" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Hero-Bild URL (optional)</Label>
              <Input className="h-10" value={f.coming_soon_hero_image_url} onChange={(e) => setF({ ...f, coming_soon_hero_image_url: e.target.value })} placeholder="https://.../hero.jpg" data-testid="service-hero-image" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Beschreibung / Was macht Ihr Unternehmen?</Label>
            <textarea className="w-full text-sm p-3 border border-black/10 rounded" rows="4" value={f.coming_soon_message} onChange={(e) => setF({ ...f, coming_soon_message: e.target.value })}
              placeholder="Wir starten in wenigen Wochen mit professioneller Mülltonnenreinigung in Ihrer Region..."
              data-testid="service-coming-soon-message" />
          </div>

          <div>
            <Label className="text-xs">Highlights / Verkaufsargumente (max. 10)</Label>
            <div className="space-y-1 mb-2">
              {f.coming_soon_features.map((feat, i) => (
                // Editierbare Liste: Index-Key ist hier korrekt (Items haben keine ID, Inhalt ändert sich beim Tippen)
                <div key={i} className="flex items-center gap-2 text-sm bg-zinc-50 border border-black/10 rounded px-2 py-1.5" data-testid={`feature-item-${i}`}>
                  <span className="text-emerald-600 font-bold">✓</span>
                  <span className="flex-1">{feat}</span>
                  <button onClick={() => removeFeature(i)} className="text-red-500 hover:text-red-700" data-testid={`remove-feature-${i}`}><X size={12} /></button>
                </div>
              ))}
            </div>
            {f.coming_soon_features.length < 10 && (
              <div className="flex gap-2">
                <Input className="h-9 text-xs" value={featureDraft} onChange={(e) => setFeatureDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())} placeholder='z. B. „100% umweltfreundlich mit Bio-Reinigern"' data-testid="feature-draft" />
                <Button type="button" onClick={addFeature} className="h-9 rounded-none bg-black text-white hover:bg-zinc-800 text-xs" data-testid="add-feature-btn"><Plus size={12} className="mr-1" />Hinzu</Button>
              </div>
            )}
          </div>

          {/* Feature-Grid-Block */}
          <div className="pt-4 border-t border-black/5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-3">Feature-Grid (Warum wir?)</div>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <Input className="h-10" value={f.coming_soon_feature_grid_title} onChange={(e) => setF({ ...f, coming_soon_feature_grid_title: e.target.value })} placeholder='Titel · z. B. „Warum GTR?"' data-testid="feature-grid-title" />
              <div className="text-[10px] text-zinc-500 self-center">Bis zu 8 Feature-Karten mit Icon, Titel und Kurztext</div>
            </div>
            <div className="space-y-2 mb-2">
              {(f.coming_soon_feature_grid || []).map((item, i) => (
                <div key={i} className="grid grid-cols-[110px_1fr_2fr_28px] gap-2 items-center bg-zinc-50 border border-black/10 rounded px-2 py-2" data-testid={`grid-item-${i}`}>
                  <select value={item.icon || "sparkle"} onChange={(e) => { const arr = [...f.coming_soon_feature_grid]; arr[i] = { ...item, icon: e.target.value }; setF({ ...f, coming_soon_feature_grid: arr }); }} className="h-8 px-2 rounded border border-black/10 text-xs bg-white" data-testid={`grid-icon-${i}`}>
                    <option value="sparkle">✨ Sparkle</option>
                    <option value="map-pin">📍 Vor Ort</option>
                    <option value="recycle">♻️ Recycle</option>
                    <option value="shield">🛡️ Schutz</option>
                    <option value="clock">⏰ Zeit</option>
                    <option value="star">⭐ Qualität</option>
                    <option value="truck">🚛 Truck</option>
                    <option value="smiley">😊 Freundlich</option>
                  </select>
                  <Input className="h-8 text-xs" value={item.title || ""} onChange={(e) => { const arr = [...f.coming_soon_feature_grid]; arr[i] = { ...item, title: e.target.value }; setF({ ...f, coming_soon_feature_grid: arr }); }} placeholder="Titel" data-testid={`grid-title-${i}`} />
                  <Input className="h-8 text-xs" value={item.description || ""} onChange={(e) => { const arr = [...f.coming_soon_feature_grid]; arr[i] = { ...item, description: e.target.value }; setF({ ...f, coming_soon_feature_grid: arr }); }} placeholder="Kurzer Beschreibungstext" data-testid={`grid-desc-${i}`} />
                  <button onClick={() => { const arr = f.coming_soon_feature_grid.filter((_, idx) => idx !== i); setF({ ...f, coming_soon_feature_grid: arr }); }} className="text-red-500 hover:text-red-700 justify-self-center" data-testid={`grid-remove-${i}`}><X size={12} /></button>
                </div>
              ))}
            </div>
            {(f.coming_soon_feature_grid || []).length < 8 && (
              <Button type="button" onClick={() => setF({ ...f, coming_soon_feature_grid: [...(f.coming_soon_feature_grid || []), { icon: "sparkle", title: "", description: "" }] })} className="h-9 rounded-none bg-black text-white hover:bg-zinc-800 text-xs" data-testid="add-grid-item">
                <Plus size={12} className="mr-1" />Feature-Karte hinzufügen
              </Button>
            )}
          </div>

          {/* Prozess-Schritte-Block */}
          <div className="pt-4 border-t border-black/5">
            <div className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-3">Prozess-Schritte (So einfach geht's)</div>
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <Input className="h-10" value={f.coming_soon_process_title} onChange={(e) => setF({ ...f, coming_soon_process_title: e.target.value })} placeholder='Titel · z. B. „Drei Schritte. Kein Aufwand."' data-testid="process-title" />
              <div className="text-[10px] text-zinc-500 self-center">Bis zu 5 nummerierte Schritte</div>
            </div>
            <div className="space-y-2 mb-2">
              {(f.coming_soon_process_steps || []).map((step, i) => (
                <div key={i} className="grid grid-cols-[28px_1fr_2fr_28px] gap-2 items-center bg-zinc-50 border border-black/10 rounded px-2 py-2" data-testid={`step-item-${i}`}>
                  <span className="text-center text-xs font-bold text-zinc-500">{i + 1}</span>
                  <Input className="h-8 text-xs" value={step.title || ""} onChange={(e) => { const arr = [...f.coming_soon_process_steps]; arr[i] = { ...step, title: e.target.value }; setF({ ...f, coming_soon_process_steps: arr }); }} placeholder="Schritt-Titel" data-testid={`step-title-${i}`} />
                  <Input className="h-8 text-xs" value={step.description || ""} onChange={(e) => { const arr = [...f.coming_soon_process_steps]; arr[i] = { ...step, description: e.target.value }; setF({ ...f, coming_soon_process_steps: arr }); }} placeholder="Kurz-Beschreibung" data-testid={`step-desc-${i}`} />
                  <button onClick={() => { const arr = f.coming_soon_process_steps.filter((_, idx) => idx !== i); setF({ ...f, coming_soon_process_steps: arr }); }} className="text-red-500 hover:text-red-700 justify-self-center" data-testid={`step-remove-${i}`}><X size={12} /></button>
                </div>
              ))}
            </div>
            {(f.coming_soon_process_steps || []).length < 5 && (
              <Button type="button" onClick={() => setF({ ...f, coming_soon_process_steps: [...(f.coming_soon_process_steps || []), { title: "", description: "" }] })} className="h-9 rounded-none bg-black text-white hover:bg-zinc-800 text-xs" data-testid="add-step-item">
                <Plus size={12} className="mr-1" />Schritt hinzufügen
              </Button>
            )}
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Newsletter-Button-Text</Label>
              <Input className="h-10" value={f.coming_soon_cta_text} onChange={(e) => setF({ ...f, coming_soon_cta_text: e.target.value })} placeholder='Standard: „Benachrichtigen"' data-testid="service-cta-text" />
            </div>
            <div className="flex flex-col justify-end space-y-2">
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="toggle-newsletter">
                <input type="checkbox" checked={f.coming_soon_show_newsletter} onChange={(e) => setF({ ...f, coming_soon_show_newsletter: e.target.checked })} />
                Newsletter-Anmeldung anzeigen
              </label>
              <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="toggle-contact">
                <input type="checkbox" checked={f.coming_soon_show_contact} onChange={(e) => setF({ ...f, coming_soon_show_contact: e.target.checked })} />
                Kontaktdaten (Telefon/E-Mail) anzeigen
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        <Button onClick={save} disabled={saving} className="bg-black text-white hover:bg-zinc-800 rounded-none h-10" data-testid="service-status-save">
          {saving ? "Speichere…" : "Speichern"}
        </Button>
        {savedFeedback && (
          <span className={`text-xs font-medium ${savedFeedback.ok ? "text-emerald-700" : "text-red-600"}`} data-testid="save-feedback">
            {savedFeedback.ok ? "✓" : "✗"} {savedFeedback.msg}
          </span>
        )}
        {tenant?.slug && (
          <a href={`/portal/${tenant.slug}`} target="_blank" rel="noopener noreferrer" className="ml-auto text-xs text-zinc-600 hover:text-black underline" data-testid="preview-portal-link">
            → In neuem Tab öffnen
          </a>
        )}
      </div>
        </div>

        {/* Live Preview iframe */}
        {showPreview && isComing && previewUrl && (
          <div className="lg:sticky lg:top-4 h-[700px] flex flex-col border border-black/10 rounded overflow-hidden" data-testid="preview-container">
            <div className="bg-zinc-100 px-3 py-2 text-[10px] uppercase tracking-widest font-bold text-zinc-500 border-b border-black/10 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${iframeReady ? "bg-emerald-500 animate-pulse" : "bg-zinc-400"}`} />
                Live-Vorschau · Draft-Modus
              </span>
              <button onClick={() => { setIframeReady(false); setPreviewKey(k => k + 1); }} className="text-zinc-500 hover:text-black text-sm" title="Neu laden" data-testid="reload-preview">↻</button>
            </div>
            <iframe
              key={previewKey}
              ref={iframeRef}
              src={`${previewUrl}?_preview=1`}
              title="Portal-Vorschau"
              className="w-full flex-1 bg-white"
              data-testid="preview-iframe"
              onLoad={() => {
                // Fallback wenn iframe kein ready-signal sendet
                setTimeout(() => setIframeReady(true), 500);
              }}
            />
            <div className="bg-zinc-50 px-3 py-1.5 text-[10px] text-zinc-500 border-t border-black/10">
              Änderungen erscheinen live während des Tippens (ohne Speichern).
            </div>
          </div>
        )}
      </div>
    </section>
  );
}


// ==============================================================
// SKIP REASONS PANEL (im Fuhrpark oder Settings)
// ==============================================================
export function SkipReasonsPanel() {
  const [reasons, setReasons] = useState([]);
  const [nb, setNb] = useState({ code: "", label: "", sort_order: 100 });

  const load = async () => {
    const r = await api.get("/skip-reasons").catch(() => ({ data: [] }));
    setReasons(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!nb.code || !nb.label) return;
    await api.post("/skip-reasons", nb);
    setNb({ code: "", label: "", sort_order: 100 });
    load();
  };
  const patch = async (id, patchObj) => { await api.patch(`/skip-reasons/${id}`, patchObj); load(); };
  const del = async (id) => { if (window.confirm("Grund löschen?")) { await api.delete(`/skip-reasons/${id}`); load(); } };
  const move = async (r, delta) => { await patch(r.reason_id, { sort_order: r.sort_order + delta }); };

  return (
    <section className="bg-white border border-black/10 p-5" data-testid="skip-reasons-panel">
      <h3 className="font-heading text-lg font-semibold tracking-tight mb-4 flex items-center gap-2">
        <X size={18} weight="bold" /> Skip-Gründe (Fahrer-Auswahl)
      </h3>
      <p className="text-xs text-zinc-500 mb-4">Diese Gründe erscheinen im Fahrer-Portal, wenn eine Tonne nicht gereinigt werden konnte.</p>

      <div className="space-y-2 mb-4">
        {reasons.map(r => (
          <div key={r.reason_id} className="flex items-center gap-2 border border-black/10 p-2 rounded" data-testid={`reason-row-${r.reason_id}`}>
            <span className="text-xs text-zinc-400 font-mono w-16">{r.code}</span>
            <Input className="h-8 flex-1 text-xs" defaultValue={r.label} onBlur={(e) => patch(r.reason_id, { label: e.target.value })} data-testid={`reason-label-${r.reason_id}`} />
            <button onClick={() => move(r, -1)} className="text-zinc-500 hover:text-black"><ArrowUp size={14} /></button>
            <button onClick={() => move(r, 1)} className="text-zinc-500 hover:text-black"><ArrowDown size={14} /></button>
            <button onClick={() => patch(r.reason_id, { active: !r.active })} className={`text-xs px-2 py-1 rounded ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`}>
              {r.active ? "Aktiv" : "Inaktiv"}
            </button>
            <button onClick={() => del(r.reason_id)} className="text-red-500 hover:text-red-700"><X size={14} /></button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-6 gap-2 items-end border-t border-black/5 pt-3">
        <div className="col-span-2"><Label className="text-xs">Code</Label><Input className="h-9 text-xs" value={nb.code} onChange={(e) => setNb({ ...nb, code: e.target.value })} placeholder="z_b_frost" data-testid="skip-new-code" /></div>
        <div className="col-span-3"><Label className="text-xs">Anzeigetext</Label><Input className="h-9 text-xs" value={nb.label} onChange={(e) => setNb({ ...nb, label: e.target.value })} placeholder="Tonne eingefroren" data-testid="skip-new-label" /></div>
        <Button onClick={add} className="bg-black text-white hover:bg-zinc-800 rounded-none h-9 text-xs" data-testid="skip-new-add"><Plus size={12} className="mr-1" />Hinzufügen</Button>
      </div>
    </section>
  );
}


// ==============================================================
// PROMO CODES PANEL (Mandant konfiguriert Aktionen)
// ==============================================================
export function PromoCodesPanel() {
  const [promos, setPromos] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ code: "EARLY50", description: "Erste Anmelder erhalten 20% Rabatt", discount_percent: 20, discount_cents: null, max_uses: 50, valid_until: "", applies_to: "first_booking", show_on_portal: true });

  const load = async () => {
    const r = await api.get("/promos").catch(() => ({ data: [] }));
    setPromos(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.code) return;
    try {
      const payload = { ...f };
      if (!payload.discount_cents) delete payload.discount_cents;
      if (!payload.valid_until) delete payload.valid_until;
      await api.post("/promos", payload);
      setOpen(false);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Fehler"); }
  };
  const patch = async (id, patchObj) => { await api.patch(`/promos/${id}`, patchObj); load(); };
  const del = async (id) => { if (window.confirm("Promo löschen?")) { await api.delete(`/promos/${id}`); load(); } };

  return (
    <section className="bg-white border border-black/10 p-5" data-testid="promo-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-semibold tracking-tight flex items-center gap-2">
          <Tag size={18} weight="bold" /> Promo-Codes / Aktionen
        </h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-none bg-black text-white hover:bg-zinc-800 h-9 text-xs" data-testid="add-promo-btn"><Plus size={12} className="mr-1" />Neue Aktion</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Promo-Code anlegen</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Code</Label><Input className="h-10 uppercase font-mono" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} data-testid="promo-code-input" /></div>
              <div>
                <Label className="text-xs">Zielgruppe</Label>
                <Select value={f.applies_to} onValueChange={(v) => setF({ ...f, applies_to: v })}>
                  <SelectTrigger className="h-10" data-testid="promo-applies-to"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="first_booking">Nur Erst-Buchung</SelectItem>
                    <SelectItem value="any_booking">Jede Buchung</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2"><Label className="text-xs">Beschreibung (Text im Portal)</Label><Input className="h-10" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="promo-description" /></div>
              <div><Label className="text-xs">Rabatt %</Label><Input type="number" min="1" max="100" className="h-10" value={f.discount_percent || ""} onChange={(e) => setF({ ...f, discount_percent: e.target.value ? +e.target.value : null, discount_cents: null })} data-testid="promo-percent" /></div>
              <div><Label className="text-xs">Oder €-Betrag</Label><Input type="number" min="0" step="0.01" className="h-10" value={f.discount_cents ? f.discount_cents / 100 : ""} onChange={(e) => setF({ ...f, discount_cents: e.target.value ? Math.round(+e.target.value * 100) : null, discount_percent: null })} data-testid="promo-cents" /></div>
              <div><Label className="text-xs">Max. Einlösungen</Label><Input type="number" min="1" className="h-10" value={f.max_uses || ""} onChange={(e) => setF({ ...f, max_uses: e.target.value ? +e.target.value : null })} placeholder="z. B. 50" data-testid="promo-max" /></div>
              <div><Label className="text-xs">Gültig bis</Label><Input type="date" className="h-10" value={f.valid_until} onChange={(e) => setF({ ...f, valid_until: e.target.value })} data-testid="promo-valid-until" /></div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="promo-portal" checked={f.show_on_portal} onChange={(e) => setF({ ...f, show_on_portal: e.target.checked })} data-testid="promo-show-portal" />
                <Label htmlFor="promo-portal" className="text-xs cursor-pointer">Auf Portal & Coming-Soon-Seite anzeigen</Label>
              </div>
              <Button onClick={save} className="col-span-2 h-10 bg-black text-white hover:bg-zinc-800 rounded-none" data-testid="save-promo">Anlegen</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {promos.length === 0 ? (
        <p className="text-sm text-zinc-500">Keine Aktionen aktiv. Klicken Sie „Neue Aktion" für einen Early-Bird-Rabatt.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs text-zinc-500 uppercase">
              <tr>
                <th className="text-left py-2">Code</th>
                <th className="text-left">Rabatt</th>
                <th className="text-left">Nutzung</th>
                <th className="text-left">Gültig bis</th>
                <th className="text-left">Ziel</th>
                <th className="text-left">Portal</th>
                <th className="text-left">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {promos.map(p => {
                const usedRatio = p.max_uses ? `${p.used_count}/${p.max_uses}` : `${p.used_count}/∞`;
                return (
                  <tr key={p.promo_id} className="border-t border-black/10" data-testid={`promo-row-${p.promo_id}`}>
                    <td className="py-2 font-mono font-bold">{p.code}</td>
                    <td className="text-xs">
                      {p.discount_percent ? <span className="inline-flex items-center gap-1"><Percent size={11} weight="bold" />{p.discount_percent}%</span> : `€${(p.discount_cents / 100).toFixed(2)}`}
                    </td>
                    <td className="text-xs">{usedRatio}</td>
                    <td className="text-xs">{p.valid_until || "unbegrenzt"}</td>
                    <td className="text-xs">{p.applies_to === "first_booking" ? "Erst-Buchung" : "Jede Buchung"}</td>
                    <td className="text-xs">
                      <button onClick={() => patch(p.promo_id, { show_on_portal: !p.show_on_portal })} className={`px-2 py-0.5 rounded text-[10px] ${p.show_on_portal ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`} data-testid={`toggle-portal-${p.promo_id}`}>
                        {p.show_on_portal ? "Sichtbar" : "Versteckt"}
                      </button>
                    </td>
                    <td>
                      <button onClick={() => patch(p.promo_id, { active: !p.active })} className={`px-2 py-0.5 rounded text-[10px] ${p.active ? "bg-emerald-100 text-emerald-700" : "bg-zinc-200 text-zinc-500"}`} data-testid={`toggle-active-${p.promo_id}`}>
                        {p.active ? "Aktiv" : "Inaktiv"}
                      </button>
                    </td>
                    <td className="text-right">
                      <button onClick={() => del(p.promo_id)} className="text-red-500 hover:text-red-700" data-testid={`del-promo-${p.promo_id}`}><X size={14} /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// Import Dialog components at top of file

